import { SearchResultRecommendList } from "Scripts/global/apps/services/models/search.models";
import { LogErrorResponse } from "../Logging/LoggingAPI";
import {
    AddToCartPopupUpsellRequestDto,
    CartPageUpsellRequestDto,
    ProductPageRequestDto,
    UpsellBlockRequestDto,
} from "./esalesAPISchema";

export type UpsellSuccess = [true, SearchResultRecommendList[]];
type UpsellFailure = [false, string];
export type UpsellResult = UpsellSuccess | UpsellFailure;

const TOUCHPOINT_DESKTOP = "DESKTOP";
const TOUCHPOINT_MOBILE = "MOBILE";

export class UpsellService {
    private static readonly baseUrl = "/publicapi/esales";

    private static async getUpsell<
        TBody extends Record<string, any> = Record<string, any>,
        TQuery extends Record<string, any> = Record<string, any>,
    >(
        endpoint: string,
        body?: TBody,
        queryParams?: TQuery,
        method: "GET" | "POST" = "POST",
    ): Promise<UpsellResult> {
        let url = `${this.baseUrl}/${endpoint}`;

        const touchpoint = this.getTouchpoint();

        if (queryParams) {
            queryParams = { ...queryParams, touchpoint } as TQuery;
            const queryString = new URLSearchParams(queryParams).toString();
            url += `?${queryString}`;
        }

        if (body) {
            body = { ...body, touchpoint } as TBody;
        }

        const response = await fetch(url, {
            method,
            headers: {
                "Content-Type": "application/json",
            },
            body: body ? JSON.stringify(body) : undefined,
        });

        if (!response.ok) {
            await LogErrorResponse(url.toString(), response);
            return [
                false,
                `Failed to fetch upsell for ${endpoint}: ${response.statusText}`,
            ];
        }

        return [true, await response.json()];
    }

    private static getTouchpoint() {
        if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
            return TOUCHPOINT_MOBILE;
        }

        return TOUCHPOINT_DESKTOP;
    }

    static async getProductPageUpsell(
        payload: ProductPageRequestDto,
    ): Promise<UpsellResult> {
        return this.getUpsell<ProductPageRequestDto>(
            "product-page-upsell",
            payload,
        );
    }

    static async getAddToCartPopupUpsell(
        payload: AddToCartPopupUpsellRequestDto,
    ): Promise<UpsellResult> {
        return this.getUpsell<AddToCartPopupUpsellRequestDto>(
            "add-to-cart-popup-upsell",
            payload,
        );
    }

    static async getCartPageUpsell(
        payload: CartPageUpsellRequestDto,
    ): Promise<UpsellResult> {
        return this.getUpsell<CartPageUpsellRequestDto>(
            "cart-page-upsell",
            payload,
        );
    }

    static async getUpsellBlock(
        payload: UpsellBlockRequestDto,
    ): Promise<UpsellResult> {
        return this.getUpsell<UpsellBlockRequestDto>(
            "upsell-block",
            undefined,
            payload,
            "GET",
        );
    }
}
